import { styled } from '@spazious/dollhouse-theme';
import { PaletteColors, SizingNames } from '@spazious/dollhouse-theme';

import {
  BaseCSSPropertiesList,
  BaseExtendProps,
  createBase,
  HTMLAttributesList,
} from '../Base/index.js';

export type SelectElement = HTMLSelectElement;

export type SelectProps = Omit<
  BaseExtendProps<React.SelectHTMLAttributes<SelectElement>>,
  'size' | 'color'
> & {
  color?: PaletteColors;
  size?: SizingNames;
  fullWidth?: boolean;
};

export const SelectHTMLAttributesList: (keyof React.SelectHTMLAttributes<SelectElement>)[] = [
  ...HTMLAttributesList,
  'autoComplete',
  'disabled',
  'form',
  'multiple',
  'name',
  'required',
  'size',
  'value',
  'onChange',
];

export const SelectPropsList: (keyof SelectProps)[] = [
  ...BaseCSSPropertiesList,
  ...SelectHTMLAttributesList,
  'color',
  'size',
  'fullWidth',
];

export const Select = styled(createBase('select')).withConfig({
  shouldForwardProp: (prop) => !['color', 'size', 'fullWidth'].includes(prop),
  displayName: 'Select',
})<SelectProps>`
  --spacing-input-padding-small: 4px;
  --spacing-input-padding-medium: 8px;
  --spacing-input-padding-large: 12px;
  transition: background-color, color, box-shadow, border 0.3s ease-in-out;
  display: inline-flex;
  outline: none;
  border: none;
  margin: 0;
  padding: ${({ size = 'medium' }) => `var(--spacing-input-padding-${size})`};
  color: var(--spz-sys-color-on-surface);
  border-radius: var(--shape-rounded-radius);
  background-color: var(--spz-sys-color-surface);
  border: 1px solid var(--spz-sys-color-outline);
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  ${({ color }) => color && `color: var(--spz-sys-color-${color});`}
  &:hover {
    color: var(--spz-sys-color-on-surface-variant);
    background-color: var(--spz-sys-color-surface-variant);
    border: 1px solid var(--spz-sys-color-surface-container-high);
  }
  &:focus {
    color: var(--spz-sys-color-on-surface-variant);
    background-color: var(--spz-sys-color-surface-variant);
    border: 1px solid var(--spz-sys-color-primary);
    box-shadow: 0 0 0 1px var(--spz-sys-color-primary);
  }
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    background-color: var(--spz-sys-color-surface-container-highest);
    border: 1px solid var(--spz-sys-color-surface-container-high);
  `}
`;
